import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';

const {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiSave, FiGrid, FiList,
} = FiIcons;

const STORAGE_KEY = 'how2apply_admin_services';
const STORAGE_VERSION_KEY = 'how2apply_admin_services_version';
const CURRENT_VERSION = 2; // legacy local seed version (fallback only)

const defaultSeed = [
  // Student Services
  { id: 'jee-mains', name: 'JEE Mains', category: 'student', description: 'Engineering entrance examination applications', applicationFee: 1000, serviceFee: 100 },
  { id: 'neet', name: 'NEET', category: 'student', description: 'Medical entrance examination applications', applicationFee: 1700, serviceFee: 100 },
  { id: 'vit', name: 'VIT', category: 'student', description: 'VIT university applications', applicationFee: 1200, serviceFee: 100 },
  { id: 'amrita', name: 'AMRITA', category: 'student', description: 'Amrita university applications', applicationFee: 1000, serviceFee: 100 },
  { id: 'srm', name: 'SRM (SRMJEEE)', category: 'student', description: 'SRM Institute of Science and Technology – per phase', applicationFee: 1400, serviceFee: 100 },
  { id: 'bitsat', name: 'BITSAT', category: 'student', description: 'BITS admission test applications', applicationFee: 3500, serviceFee: 100 },
  { id: 'iiit-hyderabad', name: 'IIIT HYDERABAD', category: 'student', description: 'IIIT Hyderabad applications', applicationFee: 0, serviceFee: 100 },
  { id: 'tg-eapcet', name: 'TG EAPCET', category: 'student', description: 'Telangana EAPCET applications', applicationFee: 900, serviceFee: 100 },
  { id: 'ap-eapcet', name: 'AP EAPCET', category: 'student', description: 'Andhra Pradesh EAPCET applications', applicationFee: 600, serviceFee: 100 },
  { id: 'other-universities', name: 'Other Universities', category: 'student', description: 'Various university applications', applicationFee: 0, serviceFee: 100 },
  // Job Seeker Services
  { id: 'job-applications', name: 'Job Applications', category: 'job', description: 'Professional job application assistance', applicationFee: 300, serviceFee: 100 },
  { id: 'government-job-forms', name: 'Government Job Forms', category: 'job', description: 'Government employment applications', applicationFee: 200, serviceFee: 100 },
  { id: 'resume-building', name: 'Resume Building', category: 'job', description: 'Professional resume/CV services', applicationFee: 0, serviceFee: 200 },
  { id: 'career-guidance', name: 'Career Guidance', category: 'job', description: 'Counseling and guidance session', applicationFee: 0, serviceFee: 300 },
  // Other Services
  { id: 'document-verification', name: 'Document Verification', category: 'other', description: 'Official document verification services', applicationFee: 150, serviceFee: 100 },
  { id: 'passport-applications', name: 'Passport Applications', category: 'other', description: 'Passport application and renewal services', applicationFee: 1500, serviceFee: 100 },
  { id: 'visa-applications', name: 'Visa Applications', category: 'other', description: 'Visa application and documentation services', applicationFee: 2000, serviceFee: 100 },
  { id: 'other-government-forms', name: 'Other Government Forms', category: 'other', description: 'Various government applications', applicationFee: 200, serviceFee: 100 },
];

const categoryMeta = {
  student: { title: 'Student Services', color: 'from-blue-50 to-blue-100 border-blue-200' },
  job: { title: 'Job Seeker Services', color: 'from-green-50 to-green-100 border-green-200' },
  other: { title: 'Other Services', color: 'from-orange-50 to-orange-100 border-orange-200' },
};

const emptyForm = { id: '', name: '', category: 'student', description: '', applicationFee: '', serviceFee: '' };

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [query, setQuery] = useState('');
  const [view, setView] = useState('table'); // 'table' | 'cards'
  const [filterCategory, setFilterCategory] = useState('all');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data, error } = await supabase
          .from('services_dy3xr')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw error;

        if (data && data.length > 0) {
          // Map DB rows to UI model
          const mapped = data.map((row) => ({
            id: row.id,
            name: row.name,
            category: row.category || 'other',
            description: row.description || '',
            applicationFee: Number(row.application_fee || 0),
            serviceFee: Number(row.service_fee || 0),
          }));
          setServices(mapped);
        } else {
          // Seed initial data into DB if empty
          const seedRows = defaultSeed.map((s) => ({
            name: s.name,
            category: s.category,
            description: s.description,
            application_fee: s.applicationFee,
            service_fee: s.serviceFee,
            taxes: 0,
            processing_time: '2-4 hours',
            required_documents: [],
          }));
          const { error: insErr } = await supabase.from('services_dy3xr').insert(seedRows);
          if (insErr) throw insErr;
          // Re-fetch
          const again = await supabase.from('services_dy3xr').select('*');
          const mappedAgain = (again.data || []).map((row) => ({
            id: row.id,
            name: row.name,
            category: row.category || 'other',
            description: row.description || '',
            applicationFee: Number(row.application_fee || 0),
            serviceFee: Number(row.service_fee || 0),
          }));
          setServices(mappedAgain);
        }
      } catch (e) {
        console.error('Failed to load services from DB, falling back to local seed', e);
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          const storedVersion = Number(localStorage.getItem(STORAGE_VERSION_KEY) || 0);
          if (!raw) {
            setServices(defaultSeed);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultSeed));
            localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_VERSION));
            return;
          }
          const existing = JSON.parse(raw);
          let next = existing;
          if (storedVersion < CURRENT_VERSION) {
            const map = new Map(existing.map((s) => [s.id, s]));
            for (const s of defaultSeed) {
              if (!map.has(s.id)) map.set(s.id, s);
            }
            next = Array.from(map.values());
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_VERSION));
          }
          setServices(next);
        } catch (le) {
          console.error('Local fallback failed', le);
        }
      }
    };
    fetchServices();
  }, []);

  const isValidUuid = (value) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));
  };

  const persist = (next) => {
    // UI only (kept for legacy local fallback)
    setServices(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      localStorage.setItem(STORAGE_VERSION_KEY, String(CURRENT_VERSION));
    } catch (e) {
      console.error('Failed to save admin services (local fallback)', e);
    }
  };

  const syncLatest = () => {
    const map = new Map(services.map((s) => [s.id, s]));
    for (const s of defaultSeed) {
      if (!map.has(s.id)) {
        map.set(s.id, s);
      }
    }
    const next = Array.from(map.values());
    persist(next);
    toast.success('Synchronized with latest services');
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, id: `svc_${Date.now()}` });
    setIsModalOpen(true);
  };

  const openEdit = (svc) => {
    setEditingId(svc.id);
    setForm({ ...svc });
    setIsModalOpen(true);
  };

  const onDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const target = services.find(s => s.id === deleteId);
      // Only attempt DB delete for UUID ids (rows created in DB)
      if (target && isValidUuid(target.id)) {
        const { error } = await supabase
          .from('services_dy3xr')
          .delete()
          .eq('id', target.id);
        if (error) throw error;
      }
      const next = services.filter(s => s.id !== deleteId);
      setServices(next);
      // Persist local fallback so customer side can see changes
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (persistErr) { console.warn('Local persist failed:', persistErr); }
      toast.success('Service deleted');
    } catch (e) {
      console.error('Delete service failed:', e);
      toast.error(`Failed to delete service${e?.message ? `: ${e.message}` : ''}`);
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const saveForm = async (e) => {
    e?.preventDefault?.();
    const normalized = {
      ...form,
      applicationFee: Number(form.applicationFee || 0),
      serviceFee: Number(form.serviceFee || 0),
    };

    if (!normalized.name?.trim()) {
      toast.error('Service name is required');
      return;
    }

    try {
      if (editingId && isValidUuid(editingId)) {
        // Update existing DB row by UUID id
        const { error } = await supabase
          .from('services_dy3xr')
          .update({
            name: normalized.name,
            category: normalized.category,
            description: normalized.description,
            application_fee: normalized.applicationFee,
            service_fee: normalized.serviceFee,
          })
          .eq('id', editingId);
        if (error) throw error;
        setServices(prev => prev.map(s => s.id === editingId ? { ...normalized, id: editingId } : s));
        toast.success('Service updated');
      } else {
        // Insert new row (also covers editing legacy/local items without UUID)
        const { data, error } = await supabase
          .from('services_dy3xr')
          .insert({
            name: normalized.name,
            category: normalized.category,
            description: normalized.description,
            application_fee: normalized.applicationFee,
            service_fee: normalized.serviceFee,
            taxes: 0,
            processing_time: '2-4 hours',
            required_documents: [],
          })
          .select()
          .limit(1);
        if (error) throw error;
        const inserted = Array.isArray(data) ? data[0] : data;
        const mapped = {
          id: inserted.id,
          name: inserted.name,
          category: inserted.category || 'other',
          description: inserted.description || '',
          applicationFee: Number(inserted.application_fee || 0),
          serviceFee: Number(inserted.service_fee || 0),
        };
        setServices(prev => {
          if (editingId && !isValidUuid(editingId)) {
            // Replacing a legacy/local item: swap by editingId
            return prev.map(s => s.id === editingId ? mapped : s);
          }
          return [mapped, ...prev];
        });
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify([mapped, ...services])); } catch (persistErr) { console.warn('Local persist failed:', persistErr); }
        toast.success(editingId ? 'Service updated' : 'Service added');
      }
      setIsModalOpen(false);
      setEditingId(null);
    } catch (err) {
      console.error('Save service failed:', err);
      // Local fallback so admin changes still reflect in UI and customer selection
      try {
        const next = (() => {
          if (editingId) {
            return services.map(s => s.id === editingId ? { ...normalized, id: editingId } : s);
          }
          return [{ ...normalized, id: `local_${Date.now()}` }, ...services];
        })();
        setServices(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        toast.success('Saved locally (DB write blocked). Changes will reflect for customers.');
        setIsModalOpen(false);
        setEditingId(null);
      } catch (lf) {
        console.error('Local fallback failed:', lf);
        toast.error(`Failed to save service${err?.message ? `: ${err.message}` : ''}`);
      }
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return services.filter(s => {
      const matchesQ = !q || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
      const matchesC = filterCategory === 'all' || s.category === filterCategory;
      return matchesQ && matchesC;
    });
  }, [services, query, filterCategory]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, s) => {
      acc[s.category] = acc[s.category] || [];
      acc[s.category].push(s);
      return acc;
    }, {});
  }, [filtered]);

  const renderTable = (items) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            <th className="py-2 pr-3">Service Name</th>
            <th className="py-2 pr-3">Description</th>
            <th className="py-2 pr-3 text-right">Application Fee</th>
            <th className="py-2 pr-3 text-right">Service Fee</th>
            <th className="py-2 pr-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 pr-3 font-medium text-gray-900">{s.name}</td>
              <td className="py-3 pr-3 text-gray-600">{s.description}</td>
              <td className="py-3 pr-3 text-right">₹{Number(s.applicationFee || 0)}</td>
              <td className="py-3 pr-3 text-right">₹{Number(s.serviceFee || 0)}</td>
              <td className="py-3 pr-3 text-right">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="mr-2">
                  <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="danger" onClick={() => onDelete(s.id)}>
                  <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-1" /> Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCards = (items) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((s) => (
        <Card key={s.id} className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">{s.name}</h4>
              <p className="text-xs text-gray-500 mt-1 capitalize">Category: {s.category}</p>
            </div>
            <div className="space-x-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(s)}>
                <SafeIcon icon={FiEdit2} className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="danger" onClick={() => onDelete(s.id)}>
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-700 mt-3">{s.description}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Application Fee</p>
              <p className="font-medium">₹{Number(s.applicationFee || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Service Fee</p>
              <p className="font-medium">₹{Number(s.serviceFee || 0)}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin • Services</h1>
            <p className="text-gray-600 mt-1">Manage all services shown to customers</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setView(view === 'table' ? 'cards' : 'table')}>
              <SafeIcon icon={view === 'table' ? FiGrid : FiList} className="w-4 h-4 mr-2" />
              {view === 'table' ? 'Card View' : 'Table View'}
            </Button>
            <Button variant="outline" onClick={syncLatest}>
              Sync Latest Services
            </Button>
            <Button onClick={openAdd}>
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" /> Add New Service
            </Button>
          </div>
        </motion.div>

        {/* Search & Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center bg-gray-50 rounded-lg px-3 py-2 w-full md:w-1/2">
              <SafeIcon icon={FiSearch} className="w-4 h-4 text-gray-500 mr-2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by service or description"
                className="bg-transparent w-full outline-none text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                className="bg-gray-50 rounded-lg px-3 py-2 text-sm outline-none"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                <option value="student">Student Services</option>
                <option value="job">Job Seeker Services</option>
                <option value="other">Other Services</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Grouped Sections */}
        <div className="space-y-10">
          {['student', 'job', 'other'].map((cat) => (
            <div key={cat}>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">{categoryMeta[cat].title}</h2>
                <p className="text-gray-600">{cat === 'student' ? 'Educational and academic application services' : cat === 'job' ? 'Professional job application and career services' : 'Additional application and verification services'}</p>
              </div>
              <Card className={`p-5 bg-gradient-to-r ${categoryMeta[cat].color}`}>
                <div className="bg-white rounded-lg p-4">
                  {view === 'table' ? renderTable((grouped[cat] || [])) : renderCards((grouped[cat] || []))}
                </div>
              </Card>
            </div>
          ))}
        </div>

        {/* Add/Edit Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">{editingId ? 'Edit Service' : 'Add New Service'}</h3>
                <button onClick={() => setIsModalOpen(false)} className="p-2 rounded-full hover:bg-gray-100">
                  <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <form onSubmit={saveForm} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Service Name</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-gray-50 rounded-lg px-3 py-2 outline-none" required />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Service Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-gray-50 rounded-lg px-3 py-2 outline-none">
                      <option value="student">Student Services</option>
                      <option value="job">Job Seeker Services</option>
                      <option value="other">Other Services</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-gray-50 rounded-lg px-3 py-2 outline-none" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Application Fee</label>
                    <input type="number" min="0" value={form.applicationFee} onChange={(e) => setForm({ ...form, applicationFee: e.target.value })} className="w-full bg-gray-50 rounded-lg px-3 py-2 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Service Fee</label>
                    <input type="number" min="0" value={form.serviceFee} onChange={(e) => setForm({ ...form, serviceFee: e.target.value })} className="w-full bg-gray-50 rounded-lg px-3 py-2 outline-none" />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                  <Button type="submit">
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {confirmOpen && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete Service?</h3>
              <p className="text-sm text-gray-600">This action cannot be undone. The service will no longer appear for customers.</p>
              <div className="flex items-center justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  <SafeIcon icon={FiX} className="w-4 h-4 mr-2" /> Cancel
                </Button>
                <Button variant="danger" onClick={confirmDelete}>
                  <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServices;


